import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { ArrowLeft, DollarSign, BarChart3, TrendingUp, Tag, Instagram, Music, Facebook, Heart, MessageCircle, Share2, Eye, Loader2, ExternalLink } from 'lucide-react'
import { KPICard } from '../ui'
import { ContentPost, PostBrand } from '../../services/scoutService'
import { Cell } from './mockData'
import BrandsInPostDrawer from './BrandsInPostDrawer'
import { getCollabOwner, isViewsUnavailable, CollabBadge, UnavailableViews } from '../social-media/postAttribution'

const fmtEMV = (n: number): string =>
  n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(2)}M` : n >= 1_000 ? `$${(n / 1_000).toFixed(2)}K` : `$${(n || 0).toFixed(2)}`
const fmtNum = (n: number): string =>
  n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 1_000 ? `${(n / 1_000).toFixed(1)}K` : `${n ?? 0}`
const fmtDate = (iso: string): string => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
const humanizeCat = (k: string | null): string => (k ? k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : 'Other')

const platformIcon = (id: number) => (id === 2 ? Music : id === 11 ? Facebook : Instagram)
const platformColor = (id: number) => (id === 2 ? 'text-black' : id === 11 ? 'text-blue-600' : 'text-pink-500')
const platformBand = (id: number) => (id === 2 ? 'bg-black' : id === 11 ? 'bg-blue-600' : 'bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400')

interface Props {
  categoryLabel: string
  format?: string // omit → header shows just the label (e.g. a brand name)
  subtitle?: string
  cell: Cell
  fetchPosts: () => Promise<ContentPost[]>
  fetchPostBrands?: (post: ContentPost) => Promise<PostBrand[]> // omit + disablePostDrawer → no drawer
  thumbnailUrl?: (post: ContentPost) => string | null // omit → placeholder only
  filters?: React.ReactNode // optional filter controls rendered under the header
  disablePostDrawer?: boolean // true → posts aren't clickable, no brands-in-post drawer
  backLabel?: string // defaults to "Back to heatmap"
  onBack: () => void
}

const PAGE_SIZE = 32

const CategoryDetailView: React.FC<Props> = ({ categoryLabel, format, subtitle, cell, fetchPosts, fetchPostBrands, thumbnailUrl, filters, disablePostDrawer, backLabel, onBack }) => {
  const [posts, setPosts] = useState<ContentPost[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)

  const [selectedPost, setSelectedPost] = useState<ContentPost | null>(null)
  const [brands, setBrands] = useState<PostBrand[]>([])
  const [brandsLoading, setBrandsLoading] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      setPosts(await fetchPosts()); setPage(1)
    } catch {
      setError('Failed to load posts'); setPosts([])
    } finally {
      setLoading(false)
    }
  }, [fetchPosts])

  useEffect(() => { load() }, [load])

  const avg = cell.posts ? cell.emv / cell.posts : 0
  const distinctBrandCount = useMemo(() => posts.filter((p) => p.brand_count > 0).length, [posts])

  const totalPages = Math.max(1, Math.ceil(posts.length / PAGE_SIZE))
  const pagePosts = useMemo(() => posts.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [posts, page])

  const openPost = async (p: ContentPost) => {
    if (disablePostDrawer || !fetchPostBrands) return
    setSelectedPost(p); setBrands([]); setDrawerOpen(true); setBrandsLoading(true)
    try {
      setBrands(await fetchPostBrands(p))
    } catch {
      setBrands([])
    } finally {
      setBrandsLoading(false)
    }
  }

  return (
    <div className="space-y-4">
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 transition-colors">
        <ArrowLeft className="h-3.5 w-3.5" /> {backLabel ?? 'Back to heatmap'}
      </button>

      <div>
        <h2 className="text-lg font-bold text-gray-900">{categoryLabel}{format ? <span className="text-gray-400 font-normal"> · {format}</span> : null}</h2>
        <p className="text-sm text-gray-500">{subtitle ?? 'Posts in this category & format. Click a post to see the brands detected in it.'}</p>
      </div>

      {filters ? <div className="flex flex-wrap items-center justify-end gap-2">{filters}</div> : null}

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard label="Total EMV" value={fmtEMV(cell.emv)} icon={DollarSign} colorScheme="emerald" />
        <KPICard label="Posts" value={String(cell.posts)} icon={BarChart3} colorScheme="purple" />
        <KPICard label="Avg EMV / Post" value={fmtEMV(avg)} icon={TrendingUp} colorScheme="amber" />
        <KPICard label="Posts w/ Brands" value={String(distinctBrandCount)} icon={Tag} colorScheme="blue" />
      </div>

      {/* Post cards */}
      {loading ? (
        <div className="flex items-center justify-center min-h-[240px]"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>
      ) : error ? (
        <div className="min-h-[160px] flex items-center justify-center text-sm text-red-600">{error}</div>
      ) : posts.length === 0 ? (
        <div className="min-h-[160px] flex items-center justify-center text-sm text-gray-400">No posts for the selected filters.</div>
      ) : (
        <>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {pagePosts.map((p) => {
            const Icon = platformIcon(p.platform_id)
            const thumb = thumbnailUrl ? thumbnailUrl(p) : null
            const collabOwner = getCollabOwner(p.collaborator_username)
            const accountLabel = p.platform_id === 11 ? p.account_username : `@${p.account_username}`
            const viewsUnavailable = isViewsUnavailable({
              isInstagram: p.platform_id === 1,
              views: p.views_count,
              postType: p.post_type,
              collabOwner,
            })
            return (
              <div
                key={`${p.partition_key}-${p.post_id}`}
                onClick={disablePostDrawer ? undefined : () => openPost(p)}
                className={`bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg hover:border-gray-300 hover:-translate-y-0.5 transition-all duration-300 ${disablePostDrawer ? '' : 'cursor-pointer'}`}
              >
                <div className={`h-1.5 ${platformBand(p.platform_id)}`} />
                <div className="flex items-center gap-2 px-3 pt-2.5 pb-2">
                  <Icon className={`h-4 w-4 flex-shrink-0 ${platformColor(p.platform_id)}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 min-w-0 h-4">
                      <span className="text-xs font-semibold text-gray-900 truncate">{collabOwner ? `@${collabOwner}` : accountLabel}</span>
                      {collabOwner && <CollabBadge owner={collabOwner} creditedTo={p.account_username} />}
                    </div>
                    <div className="text-[10px] text-gray-400 truncate">
                      {collabOwner && <>with {accountLabel} · </>}
                      {fmtDate(p.posted_at)}
                    </div>
                  </div>
                </div>
                <div className="aspect-video bg-gray-100 relative flex items-center justify-center">
                  {thumb ? (
                    <img src={thumb} alt="" className="w-full h-full object-cover" crossOrigin="use-credentials" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }} />
                  ) : (
                    <Icon className={`h-8 w-8 opacity-40 ${platformColor(p.platform_id)}`} />
                  )}
                  {p.content_category && <span className="absolute bottom-2 left-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-white/90 text-gray-800">{humanizeCat(p.content_category)}</span>}
                </div>
                <div className="p-3 space-y-2">
                  {/* Post title (caption) — clamped to 2 lines; full text on hover. Models the Post-menu card. */}
                  {p.caption && (
                    <p className="text-[11px] text-gray-700 font-medium line-clamp-2 leading-snug" title={p.caption}>
                      {p.caption}
                    </p>
                  )}
                  {/* Engagement metrics — match the Post-menu card (PostCard): gray icons, this order */}
                  <div className="flex items-center gap-2.5 text-xs text-gray-500">
                    <div className="flex items-center gap-0.5"><Heart className="h-3 w-3" />{fmtNum(p.likes_count)}</div>
                    <div className="flex items-center gap-0.5"><MessageCircle className="h-3 w-3" />{fmtNum(p.comments_count)}</div>
                    <div className="flex items-center gap-0.5"><Share2 className="h-3 w-3" />{fmtNum(p.shares_count)}</div>
                    {viewsUnavailable
                      ? <UnavailableViews collabOwner={collabOwner} />
                      : <div className="flex items-center gap-0.5"><Eye className="h-3 w-3" />{fmtNum(p.views_count)}</div>}
                  </div>
                  {/* EMV + external link — match the Post-menu card */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-0.5 text-xs font-semibold text-emerald-600">
                      <DollarSign className="h-3 w-3" />
                      <span>{fmtEMV(p.emv)}</span>
                    </div>
                    {p.post_url && (
                      <a href={p.post_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-gray-400 hover:text-blue-500 transition-colors" title="View post">
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    )}
                  </div>
                  {/* Brands detected (Value-path specific) */}
                  <div className="flex items-center gap-1 text-[10px] text-gray-500">
                    <Tag className="h-3 w-3 text-indigo-500" />
                    {p.brand_count} brand{p.brand_count === 1 ? '' : 's'} detected
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between border-t border-gray-200 px-1 pt-4 mt-4">
          <div className="text-xs text-gray-500">
            Showing {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, posts.length)} of {posts.length} posts
          </div>
          {totalPages > 1 && (
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setPage(1)}
                disabled={page <= 1}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium border rounded-lg transition-colors shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
              >
                First
              </button>
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium border rounded-lg transition-colors shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
              >
                Previous
              </button>
              <span className="text-xs text-gray-600">Page {page} of {totalPages}</span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium border rounded-lg transition-colors shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
              >
                Next
              </button>
              <button
                onClick={() => setPage(totalPages)}
                disabled={page >= totalPages}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium border rounded-lg transition-colors shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
              >
                Last
              </button>
            </div>
          )}
        </div>
        </>
      )}

      {!disablePostDrawer && <BrandsInPostDrawer post={selectedPost} brands={brands} loading={brandsLoading} isOpen={drawerOpen} onClose={() => setDrawerOpen(false)} />}
    </div>
  )
}

export default CategoryDetailView
